/**
 * Projeto and Acompanhar, each one screen with tabs (docs/refactor/05-navegacao.md) instead of a dozen
 * loose items in the sidebar. The tabs come from the navigation tree, so who sees which tab is decided
 * in one place (lib/navigation.ts); the screen under each tab is the page it always was.
 *
 * Old routes (`/docs`, `/sdd`, `/live`, …) redirect here with their query kept: see `Moved`.
 */

import { useCallback, useEffect } from 'react'
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { getSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { NAV_AREAS, NAV_ICONS, spaceTabs, visibleAreas } from '../lib/navigation'
import { useActiveRepository } from '../lib/activeRepository'
import { setDockSlot } from '../lib/dockSlot'
import { EmptyState, Panel, Skeleton } from '../components/primitives'
import { ProjectCard } from '../components/ProjectCard'

function useVisibleArea(id: string) {
  const session = getSession()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const areas = visibleAreas({ role: session?.role, isDeveloper: Boolean(me?.developerId), bugHuntEnabled: me?.bugHuntEnabled !== false })
  return areas.find((area) => area.id === id) ?? null
}

const under = (pathname: string, route: string) => pathname === route || pathname.startsWith(`${route}/`)

const VIEW_KEY = 'devpulse.space.view.'

function rememberView(tab: string, to: string): void {
  try {
    localStorage.setItem(VIEW_KEY + tab, to)
  } catch {
    // Private storage: the tab opens on its first view.
  }
}

/** The view of this tab the person used last, while it is still one of its views. */
function lastView(tab: string, views: Array<{ to: string }>): string | null {
  try {
    const to = localStorage.getItem(VIEW_KEY + tab)
    return to && views.some((v) => v.to === to) ? to : null
  } catch {
    return null
  }
}

/** The tab bar, and the tab's screen under it. */
export function Space({ area: id }: { area: 'project' | 'track' | 'agents' }) {
  const { t } = useI18n()
  const location = useLocation()
  const area = useVisibleArea(id)
  const tabs = area ? spaceTabs(area) : []
  const current = tabs.find((tab) => tab.views.some((view) => under(location.pathname, view.to)))
  // The two rooms fill the screen, with the panels under the room in view (docs/refactor/25).
  const flush = location.pathname.startsWith('/track/bugs') || location.pathname.startsWith('/track/floor')
  // A tab with several views opens on the one the person used last (the rooms: docs/refactor/25).
  useEffect(() => {
    const view = current?.views.find((v) => under(location.pathname, v.to))
    if (current && view && current.views.length > 1) rememberView(current.id, view.to)
  }, [current, location.pathname])

  return (
    <div className={`space${flush ? ' space--flush' : ''}`}>
      <nav className="space__tabs" aria-label={area ? t(area.labelKey) : undefined}>
        {tabs.map((tab) => {
          const Icon = NAV_ICONS[tab.views[0]!.icon]
          return (
            <NavLink
              key={tab.id}
              to={lastView(tab.id, tab.views) ?? tab.to}
              className={() => `space__tab${current?.id === tab.id ? ' is-active' : ''}`}
              aria-current={current?.id === tab.id ? 'page' : undefined}
            >
              <Icon size={14} aria-hidden /> {t(tab.labelKey)}
            </NavLink>
          )
        })}
      </nav>
      {current && current.views.length > 1 && (
        <div className="space__views segmented" role="tablist">
          {current.views.map((view) => (
            <NavLink key={view.id} to={view.to} end={view.end} role="tab" className={({ isActive }) => `segmented__item${isActive ? ' segmented__item--active' : ''}`}>
              {t(view.labelKey)}
            </NavLink>
          ))}
        </div>
      )}
      <div className="space__body">
        <Outlet />
      </div>
    </div>
  )
}

/** `/project` and `/track` alone: their first tab this person can see. */
export function SpaceIndex({ area: id }: { area: 'project' | 'track' | 'agents' }) {
  const area = useVisibleArea(id) ?? NAV_AREAS.find((a) => a.id === id)!
  const first = spaceTabs(area)[0]
  return <Navigate to={first?.to ?? '/dashboard'} replace />
}

/** An old route: the same place in its new home, query and hash kept. */
export function Moved({ to }: { to: string }) {
  const location = useLocation()
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />
}

/** Projeto › Visão: the active project at a glance, or the ways to have one. */
export function ProjectOverview() {
  const { t } = useI18n()
  const { linked, active, choose, loaded } = useActiveRepository()
  const { data: orders } = useQuery({ queryKey: ['work-orders'], queryFn: api.workOrders.list, retry: false })

  if (!loaded) return <Skeleton height={120} />
  if (!active) {
    return (
      <Panel title={t('nav.overview')}>
        <EmptyState
          title={linked.length ? t('space.overview.choose') : t('home.projectsEmpty')}
          body={linked.length ? t('space.overview.chooseHint') : t('home.projectsEmptyHint')}
          action={
            <>
              <Link to="/start-project" className="btn btn--sm btn--primary">{t('home.startProject')}</Link>
              <Link to="/agents/repositories" className="btn btn--sm btn--ghost">{t('home.projectsLink')}</Link>
            </>
          }
        />
        {linked.length > 0 && (
          <div className="home__projects">
            {linked.map((repository) => (
              <ProjectCard key={repository.id} repository={repository} orders={orders ?? []} isActive={false} onUse={() => choose(repository.id)} />
            ))}
          </div>
        )}
      </Panel>
    )
  }
  return (
    <Panel title={active.name} subtitle={t('space.overview.hint')}>
      <ProjectCard repository={active} orders={orders ?? []} isActive onUse={() => undefined} />
    </Panel>
  )
}

/**
 * Agentes › Conversas: the prompt box as the screen — the conversations as tabs, the composer under
 * them, the answer streaming beside it. The box lays itself over this place (lib/dockSlot.ts); what is
 * drawn here shows only when it cannot (no developer profile yet).
 */
export function AgentsConversations() {
  const { t } = useI18n()
  // A ref, not an effect: React calls it with null when the screen goes away.
  const place = useCallback((element: HTMLDivElement | null) => setDockSlot(element), [])
  const { data: me, isLoading } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  return (
    <div className="agents-chat" ref={place}>
      {/* Only somebody with no agent of their own is asked to connect one; the others see the box
          lay itself here in a moment, and a placeholder until then — never "connect" by mistake. */}
      {isLoading || me?.developerId ? (
        <Skeleton height={240} />
      ) : (
        <EmptyState
          title={t('agents.chat.empty')}
          body={t('agents.chat.emptyHint')}
          action={<Link to="/agents/connect" className="btn btn--sm btn--primary">{t('nav.connectAgent')}</Link>}
        />
      )}
    </div>
  )
}
